import React, { useEffect, useRef, useState } from "react";
import { Loader2, Paperclip, X } from "lucide-react";
import { isVideo, MEDIA_ACCEPT, mediaCopy, validateAssistantMedia } from "./assistantMedia";
import styles from "./AssistantAttachment.module.css";

export default function AssistantAttachment({ file, onChange, disabled, language }) {
  const copy = mediaCopy(language);
  const input = useRef(null);
  const mounted = useRef(true);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState("");
  const [checking, setChecking] = useState(false);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  useEffect(() => {
    if (!file) { setPreview(null); return; }
    const url = URL.createObjectURL(file);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);
  const select = async (event) => {
    const selected = event.target.files?.[0];
    event.target.value = ""; // The same file can be selected again after a failure.
    if (!selected) return;
    setChecking(true);
    setError("");
    try {
      await validateAssistantMedia(selected, language);
      if (mounted.current) onChange(selected);
    } catch (failure) { if (mounted.current) setError(failure.message); }
    finally { if (mounted.current) setChecking(false); }
  };
  return <div className={styles.attachment}>
    <input ref={input} className={styles.fileInput} type="file" accept={MEDIA_ACCEPT}
      aria-label={copy.attach} disabled={disabled || checking} onChange={select} />
    <div className={styles.toolbar}>
      <button type="button" className={styles.attach} onClick={() => input.current?.click()}
        disabled={disabled || checking} aria-label={copy.attach} title={copy.attach}>
        {checking ? <Loader2 size={18} className={styles.spin} /> : <Paperclip size={18} />}
        <span>{copy.attach}</span>
      </button>
      {file && <button type="button" className={styles.remove} disabled={disabled} aria-label={copy.remove}
        onClick={() => { onChange(null); setError(""); }}><X size={18} /></button>}
    </div>
    {file && preview && <div className={styles.preview}>
      {isVideo(file) ? <video src={preview} controls playsInline preload="metadata" aria-label={copy.preview} /> :
        <img src={preview} alt={copy.preview} />}
      <span>{file.name}</span>
    </div>}
    <p className={styles.hint}>{copy.hint}</p>
    {checking && <p role="status" className={styles.hint}>{copy.checking}</p>}
    {error && <p role="alert" className={styles.error}>{error}</p>}
  </div>;
}
